import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FaTachometerAlt, FaUsers, FaServer, FaKey, FaArchive, FaComments, FaTicketAlt, FaChartLine, FaUserClock } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import GlobalSearchBar from './GlobalSearchBar';
import { hasPermission } from '../config/permissions'; 
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const Layout = ({ children }) => {
    const location = useLocation();
    const [isExpanded, setIsExpanded] = useState(false);
    const { currentUser, userRole, dynamicServerId, logout } = useAuth(); 
    const serverId = dynamicServerId;
    const [totalUnread, setTotalUnread] = useState(0);
    const canDelete = hasPermission(userRole, 'CAN_DELETE_DATA');

    React.useEffect(() => {
        const canViewGlobalDashboard = hasPermission(userRole, 'CAN_VIEW_GLOBAL_DASHBOARD');
        if (!currentUser || !canViewGlobalDashboard) return;

        const q = query(
            collection(db, 'superAdmin_chats'), 
            where('participants', 'array-contains', currentUser.uid)
        );

        const unsub = onSnapshot(q, (snapshot) => {
            let count = 0;
            snapshot.forEach(doc => {
                const data = doc.data();
                count += (data.unreadCounts?.[currentUser.uid] || 0);
            });
            setTotalUnread(count);
        });

        return () => unsub();
    }, [currentUser, userRole]);


    const [notifyPermission, setNotifyPermission] = useState(Notification.permission);
    const canManageTickets = userRole === 'Dev' || userRole === 'Support';

    const requestNotificationPermission = () => {
        Notification.requestPermission().then(permission => {
            setNotifyPermission(permission);
        });
    };

    const knownTickets = React.useRef(new Set());

    React.useEffect(() => {
        // Skip duplicate listener when already on Support Dashboard to prevent competing watch streams
        if (location.pathname === '/support') return;
        // Use primitive UID to prevent infinite re-renders
        if (!currentUser?.uid || !canManageTickets) return; 

        let isMounted = true;
        const q = query(collection(db, 'supportTickets'), where('status', '==', 'Open'));

        const unsubTickets = onSnapshot(q, (snapshot) => {
            if (!isMounted) return;
            // If the Set is empty, this is the first massive page load
            const isFirstLoad = knownTickets.current.size === 0;

            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const ticket = change.doc.data();
                    const ticketId = ticket.id || ticket.ticketId;

                    // Only process if we've never seen this ticket ID before
                    if (!knownTickets.current.has(ticketId)) {
                        knownTickets.current.add(ticketId);

                        // If it's NOT the first load, it's a genuinely new ticket!
                        if (!isFirstLoad) {
                            console.log("🔥 NEW TICKET SIGNAL FIRED: ", ticketId);
                            
                            // 1. Tell Dashboard to refresh silently
                            window.dispatchEvent(new CustomEvent('newSupportTicketArrived'));

                            // 2. Fire Push Notification
                            if (Notification.permission === 'granted') {
                                try {
                                    const notification = new Notification('New Support Ticket', {
                                        body: `[${ticket.category || 'Support'}] ${ticket.subject || 'New request'}\nFrom: ${ticket.reporter?.username || 'Customer'}`,
                                        icon: '/favicon.ico'
                                    });
                                    notification.onclick = () => window.focus();
                                } catch (e) {
                                    console.error("Browser blocked the notification:", e);
                                }
                            }
                        }
                    }
                }
            });
        }, (error) => {
            console.error("Layout tickets listener error:", error);
        });

        return () => {
            isMounted = false;
            unsubTickets();
        };
    }, [currentUser?.uid, canManageTickets, location.pathname]);

    const canViewGlobalDashboard = hasPermission(userRole, 'CAN_VIEW_GLOBAL_DASHBOARD');
    const canViewOtps = hasPermission(userRole, 'CAN_VIEW_SENSITIVE_OTPS');
    const canViewOnboarding = hasPermission(userRole, 'CAN_VIEW_ONBOARDING_PURGATORY');

    const destination = canViewGlobalDashboard 
        ? "/servers" 
        : (serverId ? `/servers/${serverId}` : "#");

    const serverLinkText = canViewGlobalDashboard ? "All Servers" : "My Dashboard";

    return (
        <div className={isExpanded ? "layout-container expanded" : "layout-container"}>
            
            <header className="top-bar">
                <div className="top-bar-logo">
                    <FaTachometerAlt />
                    <h2>{userRole ? userRole : 'Dashboard'}</h2>
                </div>
                
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                    <GlobalSearchBar />
                </div>

                <div className="top-bar-user" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {notifyPermission === 'default' && canManageTickets && (
                        <button onClick={requestNotificationPermission} style={{ padding: '6px 12px', backgroundColor: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                            🔔 Enable Notifications
                        </button>
                    )}
                    <span>{currentUser?.email}</span>
                </div>
            </header>

            <nav 
                className={isExpanded ? "sidebar expanded" : "sidebar"}
                onMouseEnter={() => setIsExpanded(true)}
                onMouseLeave={() => setIsExpanded(false)}
            >
                <ul className="sidebar-nav">
                    
                    {canViewGlobalDashboard && (
                        <li>
                            <NavLink 
                                to="/dashboard" 
                                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                            >
                                <FaTachometerAlt className="nav-icon" />
                                <span className="nav-text">Dashboard</span>
                            </NavLink>
                        </li>
                    )}

                    <li>
                        <NavLink 
                            to={destination} 
                            className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                            onClick={(e) => !canViewGlobalDashboard && !serverId && e.preventDefault()}
                        >
                            <FaServer className="nav-icon" />
                            <span className="nav-text">{serverLinkText}</span>
                        </NavLink>
                    </li>

                    {canViewGlobalDashboard && (
                        <li>
                            <NavLink 
                                to="/global-chat" 
                                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                            >
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <FaComments className="nav-icon" />
                                    {totalUnread > 0 && (
                                        <span style={{ position: 'absolute', top: '-5px', right: '-8px', backgroundColor: '#e53e3e', color: 'white', fontSize: '10px', fontWeight: 'bold', width: '16px', height: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '50%', border: '2px solid #2d3748' }}>
                                            {totalUnread > 9 ? '9+' : totalUnread}
                                        </span>
                                    )}
                                </div>
                                <span className="nav-text">Global Chat</span>
                            </NavLink>
                        </li>
                    )}

                    {canViewGlobalDashboard && (
                        <li>
                            <NavLink 
                                to="/support" 
                                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                            >
                                <FaTicketAlt className="nav-icon" />
                                <span className="nav-text">Support Tickets</span>
                            </NavLink>
                        </li>
                    )}

                    {canViewGlobalDashboard && (    
                        <li>
                            <NavLink 
                                to="/admin/support-analytics" 
                                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                            >
                                <FaChartLine className="nav-icon" />
                                <span className="nav-text">Support Stats</span>
                            </NavLink>
                        </li>
                    )}

                    {/* Only Global Viewers (Dev/Support) see the Global Directory */}
                    {canViewGlobalDashboard && (
                        <li>
                            <NavLink 
                                to="/users" 
                                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                            >
                                <FaUsers className="nav-icon" />
                                <span className="nav-text">Users Directory</span>
                            </NavLink>
                        </li>
                    )}

                    {canViewGlobalDashboard && (
                        <li>
                            <NavLink 
                                to="/audit-logs" 
                                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                            >
                                <FaArchive className="nav-icon" />
                                <span className="nav-text">Audit Logs</span>
                            </NavLink>
                        </li>
                    )}

                    {/* Dev Only: Onboarding Purgatory */}
                    {canViewOnboarding && (
                        <li>
                            <NavLink 
                                to="/onboarding-purgatory" 
                                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                            >
                                <FaUserClock className="nav-icon" />
                                <span className="nav-text">Onboarding</span>
                            </NavLink>
                        </li>
                    )}

                    {/* Dev Only: Requested OTPs */}
                    {canViewOtps && (
                        <li>
                            <NavLink 
                                to="/requested-otps" 
                                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                            >
                                <FaKey className="nav-icon" />
                                <span className="nav-text">Requested OTPs</span>
                            </NavLink>
                        </li>
                    )}

                    {/* Dev Only: Archived Servers (Uses canDelete instead of canViewGlobalDashboard) */}
                    {canDelete && (
                        <li>
                            <NavLink 
                                to="/archived-servers" 
                                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                            >
                                <FaArchive className="nav-icon" />
                                <span className="nav-text">Archived Servers</span>
                            </NavLink>
                        </li>
                    )}
                </ul>

                <div style={{ marginTop: 'auto', padding: '10px' }}>
                     <button onClick={logout} style={{ width: '100%', padding: '10px', backgroundColor: '#e53e3e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                        {isExpanded ? 'Logout' : '🛑'}
                    </button>
                </div>
            </nav>

            <main className="main-content">
                {children}
            </main>
        </div>
    );
};

export default Layout;